
import { useEffect, useState } from "react";
import Joyride, { CallBackProps, STATUS } from "react-joyride";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { showNotification } from "@/hooks/use-notification-manager";
import { useLocation } from "react-router-dom";
import { getHomeTourSteps } from "./tours/HomeTourSteps";
import { getClassesTourSteps } from "./tours/ClassesTourSteps";
import { getRoutinesTourSteps } from "./tours/RoutinesTourSteps";
import { getPaymentsTourSteps } from "./tours/PaymentsTourSteps";
import { getSettingsTourSteps } from "./tours/SettingsTourSteps";
import { getPlanTourSteps } from "./tours/PlanTourSteps";

interface IntroTourProps {
  run: boolean;
  setRun: (run: boolean) => void;
}

export const IntroTour = ({ run, setRun }: IntroTourProps) => {
  const { profile } = useProfile();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);

  const handleJoyrideCallback = async (data: CallBackProps) => {
    const { status, index, action } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (action === 'next' || action === 'prev') {
      setCurrentStep(index);
    }

    if (finishedStatuses.includes(status)) {
      try {
        await supabase
          .from('user_settings')
          .update({ has_seen_tutorial: true })
          .eq('user_id', profile?.id);
        
        if (status === STATUS.FINISHED) {
          showNotification({
            title: "¡Tutorial completado!",
            description: "Ya conoces las funciones principales de esta sección.",
            variant: "success"
          });
        }
      } catch (error) {
        console.error('Error updating tutorial status:', error);
      }
      setRun(false);
      setCurrentStep(0);
    }
  };

  const getStepsByPath = () => {
    const path = location.pathname;
    
    if (path.includes('/rutina/plan/')) {
      return getPlanTourSteps();
    }
    
    switch (path) {
      case '/':
        return getHomeTourSteps();
      case '/clases':
        return getClassesTourSteps();
      case '/rutina':
        return getRoutinesTourSteps();
      case '/pagos':
        return getPaymentsTourSteps();
      case '/configuracion':
        return getSettingsTourSteps();
      default:
        return [];
    }
  };

  const steps = getStepsByPath();

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous
      hideCloseButton
      hideBackButton
      scrollToFirstStep
      showProgress
      showSkipButton
      steps={steps}
      run={run}
      styles={{
        options: {
          arrowColor: '#2196A3',
          backgroundColor: '#2196A3',
          overlayColor: 'rgba(41, 50, 51, 0.5)',
          primaryColor: '#00C1D6',
          textColor: '#fff',
          zIndex: 1000,
        },
        buttonNext: {
          backgroundColor: '#00C1D6',
          fontSize: '14px',
          padding: '8px 16px',
        },
        buttonBack: {
          color: '#fff',
          marginRight: 10,
        },
        buttonSkip: {
          color: '#fff',
        },
      }}
      locale={{
        back: 'Anterior',
        close: 'Cerrar',
        last: 'Finalizar',
        next: 'Siguiente',
        skip: 'Saltar',
        nextLabelWithProgress: `Paso ${currentStep + 1}`
      }}
    />
  );
};
